import React from 'react';
import { Box, Stack, Snackbar } from '@mui/joy';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { EditableText } from './EditableText';
import { EmojiPickerButton } from '@/components/EmojiPickerButton';
import { InlineEditControls } from '@/components/InlineEditControls';
import { DeleteAction } from '@/components/DeleteAction';
import { useUpdateCategoryMutation, useDeleteCategoryMutation } from '../api';
import { getCategoryErrorMessage } from '../utils';

export function CategoryHeader({ category, isEditing, onEditStart, onEditEnd }) {
  const [updateCategory] = useUpdateCategoryMutation();
  const [editName, setEditName] = React.useState(category.name);
  const [editEmoji, setEditEmoji] = React.useState(category.emoji || '');
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (isEditing) {
      setEditName(category.name);
      setEditEmoji(category.emoji || '');
      setErrorMessage(null);
      setIsSaving(false);
    }
  }, [isEditing, category.name, category.emoji]);

  const handleSave = async () => {
    setIsSaving(true);
    setErrorMessage(null);
    try {
      await updateCategory({
        id: category.apiId,
        data: { name: editName, emoji: editEmoji }
      }).unwrap();
      setIsSaving(false);
      onEditEnd({ ...category, name: editName, emoji: editEmoji });
    } catch (error) {
      setIsSaving(false);
      setErrorMessage(getCategoryErrorMessage(error, 'update', 'category'));
    }
  };

  const handleCancel = () => {
    setEditName(category.name);
    setEditEmoji(category.emoji || '');
    onEditEnd();
  };

  return (    
    <Stack direction="row" gap={0.5} sx={{ alignItems: 'center', pr: 1 }}>
      <Box sx={{ cursor: 'move', display: 'flex', alignItems: 'center', touchAction: 'none' }}>
        <DragIndicatorIcon />
      </Box>
      {isEditing ? (
        <>
          <EmojiPickerButton emoji={editEmoji} onEmojiSelect={setEditEmoji} disabled={isSaving} />
          <InlineEditControls
            value={editName}
            onChange={setEditName}
            onSave={handleSave}
            onCancel={handleCancel}
            isPersisting={isSaving}
          />
        </>
      ) : (
        <>
          <EditableText
            value={category.name}
            emoji={category.emoji}
            onEdit={onEditStart}    
            sx={{ pl: 0.5 }}
          />
          <DeleteAction
            id={category.apiId}
            name={category.name}
            useDelete={useDeleteCategoryMutation}
            texts={{
              message: `Are you sure you want to delete ${category.name}? This will also delete all its subcategories.`,
            }}
            errorOverrides={{
              409: 'Cannot delete category because it contains ingredients.'
            }}
          />
        </>
      )}
      <Snackbar
        color="danger"
        variant="soft"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!errorMessage}
        onClose={() => setErrorMessage(null)}
      >
        {errorMessage}
      </Snackbar>
    </Stack>          
  );
}

